import { format, formatInTimeZone } from 'date-fns-tz';
import { Encounter } from '../entity/org/Encounter';
import { IANA_TIME_ZONE_MAP } from './constants';

export const DEFAULT_FACILITY_TIME_ZONE = 'US/Central';

export interface EncounterNumberTemplateTokenDefinition {
    label: string;
    extract: (encounter: Encounter, facilityTimeZone: string) => string | null;
}

function normalizeStringValue(value: unknown): string | null {
    if (value === null || value === undefined) {
        return null;
    }

    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : null;
}

function extractInitial(value: unknown): string | null {
    const normalized = normalizeStringValue(value);
    return normalized ? normalized.charAt(0) : null;
}

function formatDateToYYYYMMDD(value: unknown): string | null {
    if (value === null || value === undefined) {
        return null;
    }

    if (value instanceof Date) {
        return !isNaN(value.getTime()) ? format(value, 'yyyyMMdd') : null;
    }

    const normalized = String(value).trim();
    if (!normalized) {
        return null;
    }

    const alreadyFormattedMatch = normalized.match(/^(\d{8})$/);
    if (alreadyFormattedMatch) {
        return alreadyFormattedMatch[1];
    }

    const isoDateMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s].*)/);
    if (isoDateMatch) {
        return `${isoDateMatch[1]}${isoDateMatch[2]}${isoDateMatch[3]}`;
    }

    const parsed = new Date(normalized);
    return !isNaN(parsed.getTime()) ? format(parsed, 'yyyyMMdd') : null;
}

function formatTimeToHHMMSS(value: unknown): string | null {
    if (value === null || value === undefined) {
        return null;
    }

    if (value instanceof Date) {
        return !isNaN(value.getTime()) ? format(value, 'HHmmss') : null;
    }

    const normalized = String(value).trim();
    if (!normalized) {
        return null;
    }

    const compactTimeMatch = normalized.match(/^(\d{2})(\d{2})(\d{2})$/);
    if (compactTimeMatch) {
        const [_, hour, minute, second] = compactTimeMatch;
        if (isValidTimeParts(hour, minute, second)) {
            return `${hour}${minute}${second}`;
        }
        return null;
    }

    const colonTimeMatch = normalized.match(/^(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/);
    if (colonTimeMatch) {
        const [_, hour, minute, second] = colonTimeMatch;
        if (isValidTimeParts(hour, minute, second)) {
            return `${hour}${minute}${second}`;
        }
    }

    return null;
}

function isValidTimeParts(hour: string, minute: string, second: string): boolean {
    const hourValue = Number(hour);
    const minuteValue = Number(minute);
    const secondValue = Number(second);

    return (
        Number.isInteger(hourValue) &&
        Number.isInteger(minuteValue) &&
        Number.isInteger(secondValue) &&
        hourValue >= 0 &&
        hourValue <= 23 &&
        minuteValue >= 0 &&
        minuteValue <= 59 &&
        secondValue >= 0 &&
        secondValue <= 59
    );
}

function resolveIanaTimeZone(facilityTimeZone?: string): string {
    const normalizedFacilityTimeZone = normalizeStringValue(facilityTimeZone) || DEFAULT_FACILITY_TIME_ZONE;
    if (normalizedFacilityTimeZone in IANA_TIME_ZONE_MAP) {
        return IANA_TIME_ZONE_MAP[normalizedFacilityTimeZone as keyof typeof IANA_TIME_ZONE_MAP];
    }

    return IANA_TIME_ZONE_MAP[DEFAULT_FACILITY_TIME_ZONE];
}

function formatTodayDate(facilityTimeZone?: string): string {
    const ianaTimeZone = resolveIanaTimeZone(facilityTimeZone);
    return formatInTimeZone(new Date(), ianaTimeZone, 'yyyyMMdd');
}

export const ENCOUNTER_NUMBER_TEMPLATE_TOKENS: Record<string, EncounterNumberTemplateTokenDefinition> = {
    FIRST_NAME: {
        label: 'First Name',
        extract: (encounter: Encounter) => normalizeStringValue(encounter.patFrstNm),
    },
    LAST_NAME: {
        label: 'Last Name',
        extract: (encounter: Encounter) => normalizeStringValue(encounter.patLastNm),
    },
    FIRST_NAME_INITIAL: {
        label: 'First Name',
        extract: (encounter: Encounter) => extractInitial(encounter.patFrstNm),
    },
    LAST_NAME_INITIAL: {
        label: 'Last Name',
        extract: (encounter: Encounter) => extractInitial(encounter.patLastNm),
    },
    DOB_YYYYMMDD: {
        label: 'Date of Birth',
        extract: (encounter: Encounter) => formatDateToYYYYMMDD(encounter.patDob),
    },
    ADMIT_DATE_YYYYMMDD: {
        label: 'Admit Date',
        extract: (encounter: Encounter) => formatDateToYYYYMMDD(encounter.admitDt),
    },
    ADMIT_TIME_HHMMSS: {
        label: 'Admit Time',
        extract: (encounter: Encounter) => formatTimeToHHMMSS(encounter.admitTm),
    },
    TODAY_DATE_YYYYMMDD: {
        label: 'Today Date',
        extract: (_encounter: Encounter, facilityTimeZone: string) => formatTodayDate(facilityTimeZone),
    },
    ENCOUNTER_NUMBER: {
        label: 'Encounter Number',
        extract: (encounter: Encounter) => normalizeStringValue(encounter.enctrNo),
    },
    MRN: {
        label: 'MRN',
        extract: (encounter: Encounter) => normalizeStringValue(encounter.patMrn),
    },
};

const TOKEN_OPEN = '{{';
const TOKEN_CLOSE = '}}';

export function getSupportedEncounterNumberTemplateTokens(): string[] {
    return Object.keys(ENCOUNTER_NUMBER_TEMPLATE_TOKENS);
}

export function validateEncounterNumberTemplate(template: string): void {
    if (typeof template !== 'string') {
        throw new Error('encounterNumberTemplate must be a string');
    }

    const trimmedTemplate = template.trim();
    if (!trimmedTemplate) {
        return;
    }

    const tokens = extractTemplateTokens(trimmedTemplate);

    for (const token of tokens) {
        if (!ENCOUNTER_NUMBER_TEMPLATE_TOKENS[token]) {
            throw new Error(
                `encounterNumberTemplate contains unsupported token "${token}". Supported tokens: ${getSupportedEncounterNumberTemplateTokens().join(', ')}`,
            );
        }
    }
}

export function extractTemplateTokens(template: string): string[] {
    const tokens: string[] = [];
    let cursor = 0;

    while (cursor < template.length) {
        const nextOpen = template.indexOf(TOKEN_OPEN, cursor);
        const nextClose = template.indexOf(TOKEN_CLOSE, cursor);

        if (nextClose !== -1 && (nextOpen === -1 || nextClose < nextOpen)) {
            throw new Error('encounterNumberTemplate has malformed token braces');
        }

        if (nextOpen === -1) {
            break;
        }

        const endIndex = template.indexOf(TOKEN_CLOSE, nextOpen + TOKEN_OPEN.length);
        if (endIndex === -1) {
            throw new Error('encounterNumberTemplate has malformed token braces');
        }

        const tokenName = template.slice(nextOpen + TOKEN_OPEN.length, endIndex).trim();

        if (!tokenName || !/^[A-Z0-9_]+$/.test(tokenName)) {
            throw new Error('encounterNumberTemplate has malformed token syntax');
        }

        tokens.push(tokenName);
        cursor = endIndex + TOKEN_CLOSE.length;
    }

    return tokens;
}

export function renderEncounterNumberFromTemplate(params: {
    template: string;
    encounter: Encounter;
    facilityTimeZone?: string;
}): {
    encounterNumber: string;
    missingFields: string[];
} {
    const { template, encounter, facilityTimeZone } = params;
    const trimmedTemplate = template.trim();

    validateEncounterNumberTemplate(trimmedTemplate);

    const missingFields = new Set<string>();
    const tokenRegex = /{{\s*([A-Z0-9_]+)\s*}}/g;
    const rendered = trimmedTemplate.replace(tokenRegex, (_match, tokenName: string) => {
        const definition = ENCOUNTER_NUMBER_TEMPLATE_TOKENS[tokenName];
        if (!definition) {
            return _match;
        }

        const value = definition.extract(encounter, facilityTimeZone || DEFAULT_FACILITY_TIME_ZONE);
        if (!value) {
            missingFields.add(definition.label);
            return '';
        }

        return value;
    });

    return {
        encounterNumber: rendered,
        missingFields: Array.from(missingFields),
    };
}
